import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import { BreadCrumb } from 'primereact/breadcrumb';
import { MenuItem } from 'primereact/menuitem';
import StudentsDataTable from '../components/Students/StudentsDataTable';
import { Toast } from 'primereact/toast';
import { useRef, useState } from 'react';
import { ConfirmDialog } from 'primereact/confirmdialog';
import ClassWizardNewStudentDialog from '../components/ClassesRoster/ClassesRosterWizard/ClassWizardNewStudentDialog';
import { CompleteUser } from '../custom-hooks/useGetUsersBySite';
import { ClassUser } from '../models/Class';
import { useAppSelector } from '../redux/hooks';
import { selectUser } from '../redux/reducers/userSlice';

const toClassUser = (student: CompleteUser): ClassUser => ({
  id: student.id,
  firstName: student.firstName,
  lastName: student.lastName,
  email: student.emailAddress,
  publicUserId: student.publicUserId,
  externalSync: student.externalSync ? {} : undefined,
  roles: student.role?.map(role => ({ name: role.name })) ?? [],
  sites: student.site ?? [],
  classes: [],
  gradeLevels: student.grade ? [{ id: student.grade.id, name: student.grade.name }] : [],
  studentTargetGroups: student.currentRoadmap
    ? [{ id: student.currentRoadmap.id, isCurrent: true, targetGroup: student.currentRoadmap }]
    : [],
});

const Students = () => {
  const toast = useRef<Toast>(null);
  const navigate = useNavigate();
  const userRedux = useAppSelector(selectUser);
  const [openEnrollStudent, setOpenEnrollStudent] = useState<boolean>(false);
  const [selectedStudent, setSelectedStudent] = useState<CompleteUser | undefined>();
  const [enrollSite, setEnrollSite] = useState<{ id: string; name: string }>({ id: '', name: '' });
  const [refetchStudentKey, setRefetchStudentKey] = useState(0);
  const [createdStudentEmail, setCreatedStudentEmail] = useState<{ id: string; email: string } | undefined>();

  const breadcrumbItems: MenuItem[] = [
    { label: 'Set Up', command: () => navigate('/setting-up') },
    { label: 'Students' },
  ];
  const home: MenuItem = { icon: 'nc_icon-nc_home icon-sm', command: () => navigate('/') };

  return (
    <>
      <Toast ref={toast} />
      <div className="grid nc_content-frame nc_page-inner flex flex-column pb-8">
        <ConfirmDialog />
        <Toolbar
          role="section"
          className="col-10 col-offset-1 nc_page-header"
          center={<h1>Students</h1>}
          end={
            <div className="flex nc_button-row">
              <Button
                label="Enroll New Student"
                icon="nc_icon-nc_add icon-sm"
                aria-label="Enroll new student"
                rounded
                outlined
                onClick={() => {
                  setEnrollSite({ id: '', name: '' });
                  setOpenEnrollStudent(true);
                }}
                id="enrollStudentButton"
              />
            </div>
          }
        />
        <BreadCrumb className="col-10 col-offset-1 nc_breadcrumb" model={breadcrumbItems} home={home} />
        <div className="col-10 col-offset-1 nc_block">
          <StudentsDataTable
            setSelectedStudent={setSelectedStudent}
            setOpenEnrollStudent={setOpenEnrollStudent}
            refetchStudentKey={refetchStudentKey}
            createdStudentEmail={createdStudentEmail}
          />
        </div>
        {openEnrollStudent && (
          <ClassWizardNewStudentDialog
            visible={openEnrollStudent}
            onHide={() => {
              setOpenEnrollStudent(false);
              setSelectedStudent(undefined);
            }}
            siteId={selectedStudent ? selectedStudent.site?.[0]?.id ?? '' : enrollSite.id}
            siteName={selectedStudent ? selectedStudent.site?.[0]?.name ?? '' : enrollSite.name}
            siteOptions={userRedux?.sites?.map(site => ({ id: site.id || '', name: site.name || '' })) || []}
            onSiteChange={setEnrollSite}
            student={selectedStudent ? toClassUser(selectedStudent) : undefined}
            isExternal={Boolean(selectedStudent?.externalSync)}
            toast={toast}
            onCreated={student => {
              if (student.email) setCreatedStudentEmail({ id: student.id, email: student.email });
              setRefetchStudentKey(new Date().getTime());
            }}
            onSaved={() => setRefetchStudentKey(new Date().getTime())}
          />
        )}
      </div>
    </>
  );
};

export default Students;
